<template>
  <div class="finance-port-details-page">
    <div class="table-container">
      <div class="table-header">
        <h3>Port Details</h3>
        <div class="header-actions">
          <div class="search-container">
            <el-input
              v-model="searchPort"
              placeholder="port"
              size="small"
              style="width: 250px;"
              clearable
              @keyup.enter="handleSearch"
              @clear="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button size="small" type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon> Search
            </el-button>
            <el-button size="small" type="success" plain @click="loadPortDetails">
              <el-icon><Refresh /></el-icon> Refresh
            </el-button>
          </div>
        </div>
      </div>

      <!-- 主表格 - Cost of SPRO -->
      <div class="section-title">Cost of SPRO</div>
      <div class="table-wrapper" style="height:300px; overflow:auto;">
        <el-table
          :data="mainData"
          border
          style="width:100%"
          v-loading="loading"
          row-key="id"
        >
          <el-table-column type="index" label="No" width="40" align="center" />
          
          <el-table-column prop="port" label="Port" width="120">
            <template #default="{ row }">
              <span class="cell-text">{{ row.port }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="bulk_carrier" label="Bulk Carrier" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.bulk_carrier }}</span>
            </template>
          </el-table-column>
          
          <!-- Cost Bulk Carrier - 自适应列宽 -->
          <el-table-column prop="cost_bulk_carrier" label="Cost Bulk Carrier" min-width="140">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_bulk_carrier }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="oil_tanker" label="Oil Tanker" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.oil_tanker }}</span>
            </template>
          </el-table-column>
          
          <!-- Cost Oil Tanker - 自适应列宽 -->
          <el-table-column prop="cost_oil_tanker" label="Cost Oil Tanker" min-width="140">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_oil_tanker }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="lng_tanker" label="LNG Tanker" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.lng_tanker }}</span>
            </template>
          </el-table-column>
          
          <!-- Cost LNG Tanker - 自适应列宽 -->
          <el-table-column prop="cost_lng_tanker" label="Cost LNG Tanker" min-width="140">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_lng_tanker }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 成本表格 - Cost of Operations -->
      <div class="section-title">Cost of Operations</div>
      <div class="table-wrapper" style="height:250px; overflow:auto;">
        <el-table
          :data="costData"
          border
          style="width:100%"
          v-loading="loading"
          row-key="id"
        >
          <el-table-column type="index" label="No" width="40" align="center" />
          
          <el-table-column prop="port" label="Port" width="120">
            <template #default="{ row }">
              <span class="cell-text">{{ row.port }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="cost_sludge" label="Cost Sludge" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_sludge }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="cost_garbage" label="Cost Garbage" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_garbage }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="cost_mc" label="Cost MC" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_mc }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="cost_others" label="Cost Others" width="150">
            <template #default="{ row }">
              <span class="cell-text">{{ row.cost_others }}</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="remarks" label="Remarks" min-width="300">
            <template #default="{ row }">
              <span class="cell-text cell-left">{{ row.remarks }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'

const loading = ref(false)
const searchPort = ref('')
const mainData = ref([])
const costData = ref([])

const getApiBase = () => {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000'
}

const getHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

const fetchPortDetails = async (keyword) => {
  try {
    let url = `${getApiBase()}/api/auth/business-info`
    if (keyword && keyword.trim()) {
      url += `?keyword=${encodeURIComponent(keyword.trim())}`
    } else {
      return { main: [], cost: [] }
    }
    const response = await fetch(url, {
      headers: getHeaders()
    })
    if (!response.ok) {
      if (response.status === 401) {
        ElMessage.error('Authentication has expired. Please log in again.')
        return { main: [], cost: [] }
      }
      throw new Error('Failed to fetch port details')
    }
    const result = await response.json()
    return result.data || { main: [], cost: [] }
  } catch (error) {
    console.error('Error fetching port details:', error)
    throw error
  }
}

const loadPortDetails = async () => {
  if (loading.value) return
  
  const keyword = searchPort.value.trim()
  if (!keyword) {
    mainData.value = []
    costData.value = []
    return
  }
  
  loading.value = true
  try {
    const data = await fetchPortDetails(keyword)
    mainData.value = data.main || []
    costData.value = data.cost || []
    if (mainData.value.length === 0 && costData.value.length === 0) {
      ElMessage.info('No matching ports found')
    }
  } catch (error) {
    ElMessage.error('Failed to load port details')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  loadPortDetails()
}

onMounted(() => {
  mainData.value = []
  costData.value = []
})
</script>

<style scoped>
.finance-port-details-page {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.table-container {
  flex: 1;
  overflow: auto;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #ebeef5;
}

.table-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.search-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 8px 0 6px 0;
  padding-left: 8px;
  border-left: 3px solid #3498db;
}

.table-wrapper {
  height: 300px;
  overflow: auto;
  margin-bottom: 12px;
}

.cell-text {
  display: block;
  padding: 2px 6px;
  color: #303133;
  font-weight: 400;
  font-size: 12px;
  text-align: center;
  width: 100%;
  min-height: 22px;
  line-height: 22px;
}

.cell-left {
  text-align: left !important;
}

:deep(.el-table) {
  font-size: 13px;
}

:deep(.el-table .cell) {
  padding: 2px 4px;
}

:deep(.el-table td.el-table__cell) {
  padding: 2px 0 !important;
}

:deep(.el-table__header-wrapper) {
  background: #f5f7fa;
}

:deep(.el-table th.el-table__cell) {
  background-color: #3498db !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 11px !important;
  padding: 4px 0 !important;
}

:deep(.el-table th.el-table__cell .cell) {
  font-size: 11px !important;
  line-height: 20px !important;
  color: #ffffff !important;
}

:deep(.el-table__body-wrapper) {
  overflow-y: auto;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}
</style>